import { supabase } from '../lib/supabase';

export interface ApplicationDocument {
  id: string;
  inquiryId: string;
  documentType: 'proof_of_identity' | 'proof_of_residence' | 'proof_of_income' | 'veterinary_reference' | 'home_photos' | 'other';
  fileUrl: string;
  fileName: string;
  fileSize?: number;
  status: 'pending' | 'verified' | 'rejected';
  adminNotes?: string;
  userId?: string;
  uploadedAt: string;
  verifiedAt?: string;
  verifiedBy?: string;
  createdAt: string;
  updatedAt: string;
}

interface ApplicationDocumentInsert {
  inquiryId: string;
  documentType: ApplicationDocument['documentType'];
  fileUrl: string;
  fileName: string;
  fileSize?: number;
  userId: string;
}

interface ApplicationDocumentUpdate {
  status?: ApplicationDocument['status'];
  adminNotes?: string;
  verifiedAt?: string;
  verifiedBy?: string;
}

// Convert snake_case to camelCase
const toCamelCase = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(item => toCamelCase(item));
  }

  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  return Object.keys(obj).reduce((acc: any, key: string) => {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    acc[camelKey] = toCamelCase(obj[key]);
    return acc;
  }, {});
};

// Convert camelCase to snake_case
const toSnakeCase = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(item => toSnakeCase(item));
  }

  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  return Object.keys(obj).reduce((acc: any, key: string) => {
    const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj[key]);
    return acc;
  }, {});
};

export const applicationDocumentService = {
  // Upload document file and create database record
  async uploadDocument(
    inquiryId: string,
    documentType: ApplicationDocument['documentType'],
    file: File,
    userId: string
  ): Promise<{ data: ApplicationDocument | null; error: string | null }> {
    try {
      // Validate file size (max 10MB before compression)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        return { data: null, error: 'File size must be less than 10MB' };
      }

      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        return { data: null, error: 'Only PDF, JPG, and PNG files are allowed' };
      }

      // Compress image files before upload (PDFs are not compressed)
      let fileToUpload = file;
      if (file.type.startsWith('image/')) {
        try {
          const { compressImage } = await import('@/utils/imageCompression');
          fileToUpload = await compressImage(file, {
            maxSizeMB: 1,
            quality: 0.9,
            maxWidthOrHeight: 2048
          });
          console.log(`Compressed ${file.name}: ${(file.size / 1024).toFixed(2)}KB → ${(fileToUpload.size / 1024).toFixed(2)}KB`);
        } catch (compressionError) {
          console.warn('Image compression failed, uploading original:', compressionError);
          // Continue with original file if compression fails
        }
      }

      // Generate unique filename
      const fileExt = fileToUpload.name.split('.').pop();
      const timestamp = Date.now();
      const randomString = Math.random().toString(36).substring(2, 15);
      const fileName = `${inquiryId}/${timestamp}-${randomString}.${fileExt}`;

      // Upload file to Supabase Storage with retry logic
      const { retryWithBackoff } = await import('@/utils/imageCompression');

      await retryWithBackoff(async () => {
        const { error: storageError } = await supabase.storage
          .from('application-documents')
          .upload(fileName, fileToUpload, {
            cacheControl: '3600',
            upsert: false
          });

        if (storageError) {
          throw new Error(storageError.message);
        }
      }, 3, 1000);

      // Create database record
      const documentData: ApplicationDocumentInsert = {
        inquiryId,
        documentType,
        fileUrl: fileName,
        fileName: file.name,
        fileSize: file.size,
        userId: userId
      };

      const snakeCaseData = toSnakeCase(documentData);

      const { data, error } = await supabase
        .from('application_documents')
        .insert(snakeCaseData)
        .select()
        .single();

      if (error) {
        // Cleanup: Delete uploaded file if database insert fails
        await supabase.storage
          .from('application-documents')
          .remove([fileName]);

        return { data: null, error: error.message };
      }

      return { data: toCamelCase(data) as ApplicationDocument, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to upload document'
      };
    }
  },

  // Get documents for an inquiry
  async getInquiryDocuments(inquiryId: string): Promise<{ data: ApplicationDocument[] | null; error: string | null }> {
    try {
      if (!inquiryId) {
        return { data: [], error: null };
      }

      const { data, error } = await supabase
        .from('application_documents')
        .select('*')
        .eq('inquiry_id', inquiryId)
        .order('created_at', { ascending: false });

      if (error) {
        return { data: null, error: error.message };
      }

      return { data: toCamelCase(data) as ApplicationDocument[], error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to fetch documents'
      };
    }
  },

  // Update document status (admin only)
  async updateDocumentStatus(
    documentId: string,
    updates: ApplicationDocumentUpdate
  ): Promise<{ data: ApplicationDocument | null; error: string | null }> {
    try {
      const snakeCaseUpdates = toSnakeCase(updates);

      const { data, error } = await supabase
        .from('application_documents')
        .update(snakeCaseUpdates)
        .eq('id', documentId)
        .select()
        .single();

      if (error) {
        return { data: null, error: error.message };
      }

      return { data: toCamelCase(data) as ApplicationDocument, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to update document status'
      };
    }
  },

  // Delete document and file
  async deleteDocument(documentId: string): Promise<{ error: string | null }> {
    try {
      // Get document details first to delete file
      const { data: doc, error: fetchError } = await supabase
        .from('application_documents')
        .select('file_url')
        .eq('id', documentId)
        .single();

      if (fetchError) {
        return { error: fetchError.message };
      }

      // Extract object key
      const fileName = doc.file_url;

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('application-documents')
        .remove([fileName]);

      if (storageError) {
        return { error: `Failed to delete file: ${storageError.message}` };
      }

      // Delete database record
      const { error } = await supabase
        .from('application_documents')
        .delete()
        .eq('id', documentId);

      if (error) {
        return { error: error.message };
      }

      return { error: null };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to delete document'
      };
    }
  },

  // Create signed URL for viewing
  async getSignedUrl(fileKey: string, expiresInSeconds = 600): Promise<{ url: string | null; error: string | null }> {
    try {
      const { data, error } = await supabase.storage
        .from('application-documents')
        .createSignedUrl(fileKey, expiresInSeconds);
      if (error) {
        return { url: null, error: error.message };
      }
      return { url: data?.signedUrl || null, error: null };
    } catch (error) {
      return { url: null, error: error instanceof Error ? error.message : 'Failed to create signed URL' };
    }
  },

  // Check if application has all required documents
  async checkDocumentsComplete(inquiryId: string): Promise<{ complete: boolean; error: string | null }> {
    try {
      const { data, error } = await supabase
        .rpc('check_application_documents_complete', { inquiry_uuid: inquiryId });

      if (error) {
        return { complete: false, error: error.message };
      }

      return { complete: data || false, error: null };
    } catch (error) {
      return {
        complete: false,
        error: error instanceof Error ? error.message : 'Failed to check document completeness'
      };
    }
  },

  // Get document type label
  getDocumentTypeLabel(type: ApplicationDocument['documentType']): string {
    const labels: Record<ApplicationDocument['documentType'], string> = {
      'proof_of_identity': 'Proof of Identity',
      'proof_of_residence': 'Proof of Residence',
      'proof_of_income': 'Proof of Income',
      'veterinary_reference': 'Veterinary Reference',
      'home_photos': 'Home Environment Photos',
      'other': 'Other Document'
    };
    return labels[type] || type;
  }
};
